import { useDisclosure } from '@chakra-ui/react';
import { differenceInSeconds, isBefore, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBaseTable,
  CmBox,
  CmButton,
  CmCard,
  CmDeleteConfirmModal,
  CmFlex,
  CmFormLabel,
  CmLabelAndText,
  CmLongLoading,
  CmScrollBox,
  CmTableBodyRow,
  CmTd,
  CmTooltip,
  SelectForm,
} from '@/components/uiParts';
import { CmBackLink } from '@/components/uiParts/Link/CmBackLink ';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { ommisionText } from '@/lib/util';
import { BUTTON_AREA_ID } from '@/pages/menu-managements/components/TopButtonArea';
import {
  convertStrToFormatDate,
  DeliveryMethodKey,
  deliveryMethodMap,
  menuManagementJobMap,
  menuManagementJobTargetThs,
} from '@/pages/menu-managements/executions/service';
import { routes } from '@/router';
import {
  MenuManagementJobTarget,
  useMenuManagementDeleteExecution,
  useMenuManagementExecutionShow,
} from '@/services/Execution/hooks';

const MenuManagementExecutionsShowPage: React.FC = (): JSX.Element => {
  const { id: urlParamsId } = useParams<{ id: string }>();
  DateDomainService;
  const history = useHistory();
  const {
    isOpen: isOpenDeleteOpen,
    onOpen: handleOpenDeleteModal,
    onClose: handleCloseDeleteModal,
  } = useDisclosure();
  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, BUTTON_AREA_ID]);

  const { datetimeFormat } = DateDomainService;

  const [date, setDate] = useState<Date>(new Date());
  const [deliveryMethod, setDeliveryMethod] =
    useState<DeliveryMethodKey>('immediately');
  const [brandSelectOptions] = useState<SelectForm[]>([]);
  const [brandSelect, setBrandSelect] = useState<SelectForm | null>({
    label: '',
    value: '',
  });
  const [menuManagementJobTargets, setMenuManagementJobTargets] = useState<
    MenuManagementJobTarget[]
  >([]);

  const associateBrandSelectInput = {
    value: brandSelect,
    options: brandSelectOptions,
    onChange: (value: SelectForm | null) => {
      setBrandSelect(value);
    },
    onReset: () => {
      setBrandSelect({ value: '', label: '' });
    },
  };

  const [isLoading, setIsLoading] = useState(false);

  const listUrl = routes.menuManagementExecutions.path;
  const handleDelete = async () => {
    const minSeconds = differenceInSeconds(date, new Date());
    if (minSeconds <= 60) {
      return alert(
        `登録をされている日時指定が現在の日時から1分以内、もしくは時間が過ぎているため削除ができません。`
      );
    }
    await useMenuManagementDeleteExecution(urlParamsId);
    alert('メニュー配信を削除しました。');
    history.push(listUrl);
  };

  const initializeEditPageState = async () => {
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    if (associateBrand === undefined) return history.push(listUrl);
    setIsLoading(true);
    const execution = await useMenuManagementExecutionShow(urlParamsId);
    const { menu_management_job_targets } = execution;
    setMenuManagementJobTargets(menu_management_job_targets);
    const date =
      execution.scheduled_date !== null
        ? new Date(convertStrToFormatDate(execution.scheduled_date))
        : parseISO(execution.created_at);
    setDate(date);
    const method =
      execution.scheduled_date !== null ? 'dateAndTime' : 'immediately';
    setDeliveryMethod(method);
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      await initializeEditPageState();
    })();
  }, []);

  return (
    <PageLayout
      title={routes.menuManagementExecutionsEdit.title}
      associateBrandSelectInput={{
        ...associateBrandSelectInput,
        isDisabled: true,
      }}
    >
      <CmFlex
        id={BUTTON_AREA_ID}
        justifyContent="space-between"
        padding="20px 20px 20px 0"
        alignItems="center"
      >
        <CmBackLink to={listUrl}>一覧へ戻る</CmBackLink>

        {deliveryMethod === 'dateAndTime' && !isBefore(date, new Date()) && (
          // @ts-ignore TS2590
          <CmButton colorScheme="red" onClick={handleOpenDeleteModal}>
            削除
          </CmButton>
        )}
        <CmDeleteConfirmModal
          handleClick={handleDelete}
          {...{ name: 'メニュー配信' }}
          isOpen={isOpenDeleteOpen}
          onClose={handleCloseDeleteModal}
        />
      </CmFlex>
      <CmCard padding="20px" height={`calc(100vh - ${targetsHeight}px - 20px)`}>
        <CmScrollBox>
          <CmBox>
            <CmLabelAndText
              label={'実行方法'}
              name={deliveryMethodMap[deliveryMethod]}
            />
          </CmBox>
          <CmBox mt="20px">
            <CmLabelAndText label={`日時`} name={datetimeFormat(date)} />
          </CmBox>
          <CmFormLabel mt="20px" fontWeight="bold" mb="0">
            配信済み店舗一覧
          </CmFormLabel>
          <CmBox padding="20px">
            <CmScrollBox height={`400px`}>
              <CmBaseTable ths={menuManagementJobTargetThs}>
                {menuManagementJobTargets.map((JobTarget) => {
                  // @ts-ignore TS7053
                  const status = menuManagementJobMap[JobTarget.status];
                  return (
                    <CmTableBodyRow key={JobTarget.id}>
                      <CmTd textAlign="center">
                        <CmTooltip label={JobTarget.master_store_name}>
                          {ommisionText(JobTarget.master_store_name, 20)}
                        </CmTooltip>
                      </CmTd>
                      <CmTd textAlign="center">{status}</CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTable>
            </CmScrollBox>
          </CmBox>
        </CmScrollBox>
      </CmCard>

      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default MenuManagementExecutionsShowPage;
